import Vue from 'vue'
import { mapGetters, mapActions } from 'vuex'
import { userPositionMarker } from 'js/lib/planner'
import TopologyRoutesService from 'js/services/api/topology/routes.js'
import TopologySublinesService from 'js/services/api/topology/sublines.js'

import MapMixin from 'js/mixins/map.js'
import VueBarMixin from 'js/mixins/vue-bar.js'

import DownloadFileMixin from 'js/mixins/download-file.js'
import BeforeLinesContent from 'js/pages/routes/before-lines-content/index.js'
import StopsIndex from './show/index.js'
import SchedulesIndex from './schedules/index.js'
import SublineSchedules from './subline-schedules/index.js'
import {
  shouldShowOperatorFilter,
  filterSublinesBySelectedOperators
} from 'js/lib/routes-operator-filter.js'

import BusesMixin from './_buses_mixin.js'
import IncidencesMixin from './_incidences_mixin.js'
import MarkersMixin from './_markers_mixin'
import NearButtonMixin from './_near_button_mixin.js'
import ShapesMixin from './_shapes_mixin.js'
import SidepanelMixin from './_sidepanel_mixin.js'
import StopsMixin from './_stops_mixin.js'

import template from './index.pug'

// Bounded automatic retry for the sublines request (network error / 5xx only)
const SUBLINES_MAX_ATTEMPTS = 2
const SUBLINES_RETRY_DELAY_MS = 2000

const isRetryableSublinesError = error => {
  const response = (error && error.response) || {}
  const status = error && error.status !== undefined ? error.status : response.status

  return !status || status >= 500
}

export default Vue.extend({
  name: 'routes-index-page',
  template: template(),
  mixins: [
    MapMixin, VueBarMixin, BusesMixin, IncidencesMixin, MarkersMixin,
    NearButtonMixin, ShapesMixin, SidepanelMixin, StopsMixin, DownloadFileMixin
  ],
  components: {StopsIndex, SchedulesIndex, SublineSchedules, BeforeLinesContent},

  data() {
    return {
      // Tab seleccionado
      tab: 1,
      // Sublíneas del municipio
      sublines: [],
      // Rutas de la sublínea seleccionada
      routes: [],
      // Rutas favoritas del usuario
      favoriteRoutes: [],
      // Sublínea seleccionada
      selectedSublineId: undefined,
      // Ruta seleccionada
      selectedRouteId: undefined,
      loadingRoute: false,
      showSchedules: false,
      // Modal filtro operadores (como Mobile)
      showOperatorModal: false,

      // Tab seleccionado del show de sublínea
      sublineTab: 1,
      opcionSelectotLinea:0
    }
  },

  computed: {
    ...mapGetters(['userPosition', 'operators']),
    ...mapGetters('appConfig', ['currentAppConfig']),
    ...mapGetters('runtimeConfig', ['apiHost']),

    showOperatorFilter() {
      const appCode = (this.currentAppConfig && this.currentAppConfig.code || '')
      return shouldShowOperatorFilter(appCode, this.operators)
    },

    hasSelectedOperators() {
      return (this.operators || []).some(operator => operator.selected)
    },

    operatorFilterPlaceholder() {
      return this.hasSelectedOperators
        ? this.$t('routes.operatorsSeveral')
        : this.$t('routes.selectionsOperatios')
    },

    // Opciones de sublíneas (filtradas por operador seleccionado, como Mobile)
    sublinesOptions() {
      const normalizeSpaces = (s) =>
        String(s || '')
          .replace(/[ \t\r\n\f\v\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]+/g, ' ')
          .trim()

      const filteredSublines = filterSublinesBySelectedOperators(
        this.sublines,
        this.operators,
        this.showOperatorFilter
      )

      return _.sortBy(filteredSublines, subline => {
        const number = parseInt(subline.name.match(/^\d+$/))
        return number || subline.name
      }).map(subline => ({
        label: normalizeSpaces(Vue.options.filters.selectSublineName(subline)),
        value: subline.id
      }))
    },

    // Tabs del sidepanel
    tabItems() {
      return [
        {text: this.$t('routes.tabs.favorites')},
        {text: this.$t('routes.tabs.all')}
      ]
    },

    // Tabs del show de sublínea
    sublineTabItems() {
      return [
        {text: this.$t('routes.sublineTabs.schedules')},
        {text: this.$t('routes.sublineTabs.routes')}
      ]
    },

    userIcon() {
      return userPositionMarker
    },

    // Rutas que van a aparecer en el mapa
    routesToShow() {
      if (!this.selectedRouteId) return this.filteredRoutes

      if (this.selectedRoute) return [this.selectedRoute]

      if (this.showOpen || this.showSchedules) return [this.routeWithoutFav]

      return this.filteredRoutes
    },

    routesToShowHash() {
      if (!this.selectedRouteId) return this.filteredRoutesHash

      if (this.selectedRoute) return {[this.selectedRouteId]: this.selectedRoute}

      if (this.showOpen || this.showSchedules) {
        return {[this.routeWithoutFav.id]: this.routeWithoutFav}
      }

      return this.filteredRoutesHash
    },

    selectedSublineRouteIds() {
      return this.routes.map(route => route.id)
    },

    // Ruta seleccionada
    selectedRoute() {
      // Si estoy en la pestaña de favoritos, se filtra el listado por los favoritos
      if (this.tab === 0) {
        return this.favoriteRoutes.find(route => route.id === this.selectedRouteId)
      }
      return this.routes.find(route => route.id === this.selectedRouteId)
    },

    // Sublínea seleccionada
    selectedSubline() {
      return this.sublines.find(subline => subline.id === this.selectedSublineId)
    },

    // Rutas favoritas del usuario
    userFavorites() {
      if (
        this.$user && this.$user.favorite_route_ids &&
        this.$user.favorite_route_ids.constructor === Array
      ) {
        return this.$user.favorite_route_ids
      }
      return []
    },

    // Rutas que van a aparecer en el sidepanel
    filteredRoutes() {
      // Si estoy en la pestaña de favoritos, se filtra el listado por los favoritos
      if (this.tab === 0) {
        return this.favoriteRoutes
      }

      // Si existe una sublínea seleccionada, se filtran las rutas de esa sublínea
      if (this.selectedSublineId) {
        return this.routes.filter(route => route.subline_id === this.selectedSublineId)
      }

      return this.routes
    },

    filteredRoutesHash() {
      const object = {}

      this.filteredRoutes.forEach(route => {
        object[route.id] = route
      })

      return object
    },

    // Sublíneas que van a aparecer en el sidepanel
    filteredSublines() {
      return _.map(_.groupBy(this.filteredRoutes, 'subline_id'), routes => {
        const parsedRoutes = _.map(routes, route => ({
          id: route.id,
          color: route.color,
          text_color: route.text_color,
          description1: route.description1,
          description2: route.description2,
          destination: route.destination,
          origin: route.origin,
          name: route.name,
          incidences: route.incidences
        }))

        return {
          description: routes[0].subline_description,
          extension: routes[0].subline_extension,
          name: routes[0].subline_name,
          color: routes[0].subline_color,
          id: routes[0].subline_id,
          operation_program_type: routes[0].operation_program_type,
          routes: parsedRoutes
        }
      })
    },

    // Una sublínea ya seleccionada en el store
    alreadySelectedSublineId() {
      return this.$store.state.selectedSublineId
    },

    // Una ruta ya seleccionada en el store
    alreadySelectedRouteId() {
      // We handle the option when using route push
      if(this.$route.params.routeId !== undefined) {
        return this.$route.params.routeId
      }

      return this.$store.state.selectedRouteId
    },

    // Comprueba si la sublínea ya seleccionada en el store está disponible en el nuevo listado
    alreadySelectedSubline() {
      if (this.alreadySelectedSublineId) {
        return this.sublinesOptions.find(subline => subline.value === this.alreadySelectedSublineId)
      }

      return false
    },

    querySublineId() {
      return this.$route.query.sublineId
    },

    querySubline() {
      return this.sublinesOptions.find(subline => subline.value === this.querySublineId)
    },

    defaultSublineId() {
      if(this.$route.params.sublineId !== undefined) {
        return this.$route.params.sublineId
      }
      if (this.querySubline) return this.querySublineId
      if (this.sublinesOptions.length) return this.sublinesOptions[0].value

      return undefined
    },

    routeWithoutFav() {
      let route = this.favoriteRoutes.find(r => r.id === this.selectedRouteId)
      if (!route) {
        route = this.routes.find(r => r.id === this.selectedRouteId)
      }

      return route
    },

    jumpToFavoritesOnCreated() {
      return !this.querySublineId
    },

    currentSubline() {
      if (this.sublines.length && this.selectedSublineId) {
        const currentSubline = this.sublines.filter(
          subline => subline.id === this.selectedSublineId
        )[0]

        return currentSubline
      } return null
    },

    // Indica si la sublinea seleccionada tiene un pdf de horarios
    currentSublineHasPdf() {
      if (this.sublines.length && this.selectedSublineId) {
        return !!this.currentSubline.schedule_pdf_file.url
      } return false
    },

    showGoToSublineShowButton() {
      return this.selectedSublineId && this.selectedRouteId
    },

    defaultSublineTab() {
      if (this.selectedSubline) {
        return 0
      }
      return 1
    },

    routeIds() {
      return this.routes.map(r => r.id)
    }
  },

  watch: {
    selectedRouteId(newSelectedRouteId, oldSelectedRouteId) {
      // Si hay ruta, desubscribo de sublínea
      if (newSelectedRouteId) {
        this.disconnectSublineWebSocket()
      }
      if (this.alreadySelectedRouteId !== newSelectedRouteId) {
        this.$store.commit('setSelectedRouteId', newSelectedRouteId)
      }
      if (oldSelectedRouteId !== newSelectedRouteId) {
        this.getRouteStops(newSelectedRouteId)
        this.getRouteBuses(newSelectedRouteId)
      }
      this.closePopups()
      this.nextBuses = undefined
      this.routePoint = undefined
      this.routePointMarker = undefined
      // Si no hay ruta, subscribo a sublínea
      if (!newSelectedRouteId) {
        this.getSublineBuses(this.selectedSublineId)
      }
    },

    async selectedSublineId(newSelectedSublineId, oldSelectedSublineId) {
      if (this.alreadySelectedSublineId !== newSelectedSublineId) {
        this.$store.commit('setSelectedSublineId', newSelectedSublineId)
      }
      if (oldSelectedSublineId !== newSelectedSublineId) {
        this.getSublineBuses(newSelectedSublineId)
      }
      await this.getRoutes()
      this.getSublinesStops({route_ids: this.routeIds})
      this.sublineTab = this.defaultSublineTab
    },

    sidePanelOpen() {
      setTimeout(() => this.map.invalidateSize(true), 250)
    },

    tab(newTab) {
      if (newTab === 1 && this.selectedRouteId) {
        this.managePreviouslySelectedRoute(this.routes)
      }
    },

    // Si inicio sesión, debo cargar los favoritos
    $user(newUser, oldUser) {
      if (newUser && !oldUser) this.getFavoritesRoutes(this.jumpToFavoritesOnCreated)
    },

    sublinesOptions(newOptions) {
      if (!newOptions.length) {
        this.selectedSublineId = undefined
        return
      }
      const stillValid = newOptions.some(option => option.value === this.selectedSublineId)
      if (!stillValid) {
        this.selectedSublineId = this.defaultSublineId
      }
    }
  },

  created() {
    this.ensureOperatorsLoaded()
    this.getSublines()
    this.getFavoritesRoutes(this.jumpToFavoritesOnCreated)
  },

  beforeDestroy() {
    this.sublinesDestroyed = true
    this.sublinesLoading = false
    clearTimeout(this.sublinesRetryTimer)
    this.sublinesRetryTimer = null
  },

  methods: {
    ...mapActions(['getOperators']),

    async ensureOperatorsLoaded() {
      // Siempre recargar: una carga previa sin AA deja 0/1 operador y oculta el filtro.
      await this.getOperators()
    },

    openOperatorModal() {
      this.showOperatorModal = true
    },

    closeOperatorModal() {
      this.showOperatorModal = false
    },

    applyOperators() {
      this.showOperatorModal = false
    },

    removeOperator(operator) {
      if (!operator) return
      this.$set(operator, 'selected', false)
    },

    /*
     * Obtiene las rutas del municipio
     */
    async getRoutes() {
      if (this.selectedSublineId) {
        const params = {
          municipality_id: this.$municipality.id,
          subline_id: this.selectedSublineId,
          order_by: 'direction',
          order_dir: 'asc',
          active: 1,
          page: 1,
          per: 0
        }

        const {data} = await TopologyRoutesService.index(params)
        this.manageRoutes(data, false, false)
        this.cargarContraste()
      } else {
        this.routes = []
      }
    },

    /*
     * Obtiene las rutas favoritas del usuario
     */
    getFavoritesRoutes(jumpToFavorites = false) {
      if (this.$user) {
        const params = {
          municipality_id: this.$municipality.id,
          order_by: 'direction',
          order_dir: 'asc',
          active: 1,
          page: 1,
          per: 0
        }

        TopologyRoutesService.favorites({params, skipLoading: true}).then(response => {
          this.manageRoutes(response.data, true, jumpToFavorites)
        })
      } else {
        this.favoriteRoutes = []
      }
    },

    /*
     * Procesamiento tras recibir las rutas
     */
    manageRoutes(recievedRoutes, favorites, jumpToFavorites) {
      // Representación
      let routes = recievedRoutes.map(route => ({
        code: route.code,
        id: route.id,
        color: route.color,
        text_color: route.text_color,
        description1: route.description1,
        description2: route.description2,
        destination: route.destination,
        direction: route.direction,
        origin: route.origin,
        name: route.name,
        disabled: !route.subline_information,
        short_name: route.short_name,
        subline_description: route.subline_description,
        subline_extension: route.subline_extension,
        subline_name: route.subline_name,
        subline_color: route.subline_primary_color,
        subline_id: route.subline_id,
        incidences: 0,
        encoded_shape: route.encoded_shape,
        operation_program_type: route.line_operation_program_type,
        operator_ids: route.operator_ids,
      }))

      // Ordenación
      routes = _.sortBy(routes, 'subline_name')

      // Almacenamiento
      if (favorites) {
        this.favoriteRoutes = routes
        this.getAllRouteShapes(this.favoriteRoutes)
        this.getAllRouteIncidences(this.favoriteRoutes)
      } else {
        this.routes = routes
        this.getAllRouteShapes(this.routes)
        this.getAllRouteIncidences(this.routes)
        this.managePreviouslySelectedRoute(this.routes)
      }

      if (jumpToFavorites) this.selectFavoriteTab()
    },

    managePreviouslySelectedRoute(routes) {
      if (this.alreadySelectedRouteId) {
        const selectedRoute = routes.find(route => route.id === this.alreadySelectedRouteId)

        if (selectedRoute) {
          if (this.$isPortable) this.sidePanelOpen = false

          this.selectedRouteId = this.alreadySelectedRouteId
        } else {
          this.selectedRouteId = undefined
        }
      }
    },

    /*
     * Selecciona la tab de ruta favorita
     */
    selectFavoriteTab() {
      this.$nextTick(() => { if (this.favoriteRoutes.length) this.tab = 0 })
    },

    /*
     * Obtiene las sublíneas del municipio
     */
    getSublines(attempt = 1) {
      // Non-reactive guards: avoid duplicate loads and work after destroy
      if (this.sublinesDestroyed) return
      if (attempt === 1) {
        if (this.sublinesLoading) return
        this.sublinesLoading = true
      }

      const params = {
        municipality_id: this.$municipality.id,
        order_by: 'name',
        order_dir: 'asc',
        with_any_active_route: 1,
        // Igual que Mobile (all-routes): 'basic' incluye operator_ids;
        // 'passenger' no los devuelve y el filtro deja 0 líneas.
        template: 'basic',
        per: 0
      }

      const normalizeSpaces = (s) =>
        String(s || '')
          .replace(/[ \t\r\n\f\v\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]+/g, ' ')
          .trim()

      TopologySublinesService.index(params).then(response => {
        if (this.sublinesDestroyed) return
        this.sublinesLoading = false
        // clean the origin
        this.sublines = response.data.map(s => ({
          ...s,
          name: normalizeSpaces(s.name),
          description: normalizeSpaces(s.description),
          line_name: normalizeSpaces(s.line_name)
        }))

        this.selectedSublineId = this.sublinesOptions.length ? this.defaultSublineId : undefined
      }, error => {
        if (this.sublinesDestroyed) return
        if (attempt < SUBLINES_MAX_ATTEMPTS && isRetryableSublinesError(error)) {
          this.sublinesRetryTimer = setTimeout(() => {
            this.sublinesRetryTimer = null
            this.getSublines(attempt + 1)
          }, SUBLINES_RETRY_DELAY_MS)

          return
        }
        // Final failure: keep current behaviour (empty options), no unhandled rejection
        this.sublinesLoading = false
      })
    },

    /*
     * Devuelve el estilo de la ruta que se le pasa al 'ns-leaflet-geojson'
     */
    styleRoute(route) {
      return () => ({
        color: route.subline_color || '#1e1e1e',
        opacity: 0.7,
        weight: 3
      })
    },

    changeTab(index) {
      if (index !== this.tab) {
        this.tab = index
        this.loadingRoute = true
        this.$nextTick(() => {
          this.loadingRoute = false
        })
      }
    },

    changeSublineTab(index) {
      if (index !== this.sublineTab) {
        this.sublineTab = index
      }
      if (index === 0 && this.selectedRouteId) {
        this.selectedRouteId = undefined
      }
    },

    isFavorite(route) {
      return _.includes(this.userFavorites, route.id)
    },

    favoriteClass(route) {
      if (this.isFavorite(route)) {
        return 'is-favourite'
      }
      return undefined
    },

    setFavorite(route) {
      const user = this.$user
      let updatedIds = this.userFavorites

      if (updatedIds.includes(route.id)) {
        updatedIds = _.filter(updatedIds, item => item !== route.id)
      } else {
        updatedIds.push(route.id)
      }

      this.$auth.update({favorite_route_ids: updatedIds}, true).then(() => {
        user.favorite_route_ids = updatedIds
        this.$store.commit('setCurrentUser', user)
        this.getFavoritesRoutes()
        this.loadingRoute = true
        this.$nextTick(() => {
          this.loadingRoute = false
        })
        this.cargarContraste()

      })
    },

    goToSchedulesIndex(routeId) {
      this.selectedRouteId = routeId
      this.showOpen = true
      this.showSchedules = true
    },

    isExpeditionOperationProgramRoute(route) {
      return route.operation_program_type === 'expedition'
    },

    download() {
      const url = `${this.apiHost}/topology/sublines/${this.selectedSublineId}/download_file`
      const filename = this.currentSubline.schedule_pdf_file.url.split('/').pop()
      this.testUrl(url).then(result => {
        if(result){
          this.downloadFile(url, filename, true)
        } 
      })
    },

    goToSublineShow() {
      this.selectedRouteId = undefined
      this.tab = 1
      this.showOpen = false
      this.showSchedules = false
    },

    routeName() {
      return this.$t(
        'routes.name',
        {name: this.routesToShow[0].subline_name, extension: this.routesToShow[0].subline_extension}
      )
    },

    cargarContraste(){
      setTimeout(
        () => {
          this.applyContrasteCss()
      }, 500)

    },

    desplegarOpciones(){
      this.selectedSublineId=this.sublinesOptions[1].value
    },

    applyContrasteCss(){
      if(this.contraste){
        $(".ns-layout ,.ns-layout__header, .text-black , .text-gray2 , .sidepanel-map__card--item__stop--route , .sidepanel-map__card--item__stop ,.flex-middle,.ns-layout__footer__footer").css("cssText" ," color: #ffffff !important")
        $(".ns-layout ,.ns-layout__header, .text-black , .text-gray2 , .sidepanel-map__card--item__stop--route , .sidepanel-map__card--item__stop").css("background-color" ,"#1e1e1e")
        $(".text-primary , .text-gray2-hover , .button-phone , .text-gray3 , .ns-layout__sidebar__map__menu").css("cssText" ," color: #ffffff !important")
        $(".text-primary , .text-gray2-hover , .button-phone , .text-gray3 ").css("background-color" ,"#1e1e1e")
        $("a ,.sidepanel-map__container, .recharge-home__recharges , .recharge-home__cities , .ns-layout__footer__items , .pb-d6 , .sidepanel-map__card--item__point-name").css("color" ,"#fff")
        $(".sidepanel-map__container, .recharge-home__recharges , .recharge-home__cities , .ns-layout__footer__items ,.pb-d6, .sidepanel-map__card--item__point-name,.flex-middle ,.sidepanel-map__header").css( "cssText" ,"background-color: #1e1e1e !important")
        $(".sidepanel-map--planner__form , .uppercase-vz ,.hidden-xs ,.panel,.ns-field__label,.uppercase ,#fares-side-menu,.flex-column").css("color" ,"#fff")
        $(".sidepanel-map--planner__form , .uppercase-vz ,.hidden-xs ,.panel,.ns-field__label,.uppercase ,#fares-side-menu,.flex-column").css("background-color" ,"#1e1e1e")
        $("label,.ns-tabs__item,.primay-dark,.text-small, .ns-icon-angle-right ,.h1-main-title,.text-main-title ").css("color" ,"#fff")
        $("label,.ns-tabs__item,.primay-dark,.text-small, .ns-icon-angle-right ,.h1-main-title,.text-main-title,.colorFondo ").css("background-color" ,"#1e1e1e")
        $(".ns-layout__header--city , .ns-button,.text-info , .ns-icon-youtube").css("color" ,"#1e1e1e");
        $(".ns-layout__header--city , .ns-button,.text-info , .ns-icon-youtube").css("background-color" ,"#fff")
        $(" .pb-d6 ").css("color" ,"#fff")  
        $("#iconorestablecer").css("color" ,"#1e1e1e");
        $("#iconorestablecer").css("background-color" ,"#fff")
        $("#textorestablecer").css("color" ,"#1e1e1e");
        $("#textorestablecer").css("background-color" ,"#fff")
        $(".text-gray2,.text-primary-hover ,.ns-layout__footer, .ns-layout__footer__footer,.hidden-cl ").css("cssText" ," color: #ffffff !important")
        $(".panel-schedules ,.px-d2 , .ns-session__nav--login ,.ns-layout__footer__footer").css( "cssText" ,"background-color: #1e1e1e !important")
        $(".ns-tabs ").css("background-color" ,"#1e1e1e")
        $("#linkhome ").css( "cssText" ,"color: #1e1e1e !important")
      
        $(".tablacss ").css( "cssText" ,"color: #1e1e1e !important")
        $(".tablacss ").css("background-color" ,"#fff")
        $(".ns-icon-angle-left,.sidepanel-map__card--item__stop ").css("color" ,"#fff")
        $(".ns-icon-angle-left ").css("background-color" ,"#1e1e1e")
        $("#linkhome ").css( "cssText" ,"color: #1e1e1e !important")
        $(".ns-layout__sidebar__map__menu").css("background-color" ,"#ffffff")
        $(".textomenu ").css( "cssText" ,"color: #1e1e1e !important")
        $("#linkhome ").css( "cssText" ,"color: #1e1e1e !important")
        $(".ns-layout__sidebar__map__menu").css("background-color" ,"#ffffff")
        $(".textomenu ").css( "cssText" ,"color: #1e1e1e !important")

        $(".submenuusuario").css("background-color" ,"#ffffff")
        $(".submenuusuario").css( "cssText" ,"color: #1e1e1e !important")

        $(".button-phone").css("border", "2px solid #ffffff")
        $(".option-menu").css("border", "none")
        $( ".active" ).css("background-color","transparent").css("border","2px solid #1e1e1e");
      }else{
        
        $(".ns-layout ,.ns-layout__header, .text-black , .text-gray2 , .sidepanel-map__card--item__stop--route , .sidepanel-map__card--item__stop").css("color" ,"")
        $(".ns-layout ,.ns-layout__header, .text-black , .text-gray2 , .sidepanel-map__card--item__stop--route , .sidepanel-map__card--item__stop").css("background-color" ,"")
        $(".text-primary , .text-gray2-hover , .button-phone , .text-gray3 , .ns-layout__sidebar__map__menu").css("color" ,"")
        $(".text-primary , .text-gray2-hover , .button-phone , .text-gray3 , .ns-layout__sidebar__map__menu").css("background-color" ,"")
        $("a ,.sidepanel-map__container, .recharge-home__recharges , .recharge-home__cities , .ns-layout__footer__items , .pb-d6 , .sidepanel-map__card--item__point-name").css("color" ,"")
        $(".sidepanel-map__container, .recharge-home__recharges , .recharge-home__cities , .ns-layout__footer__items ,.pb-d6, .sidepanel-map__card--item__point-name,.flex-middle ,.sidepanel-map__header").css( "background-color" ,"")
        $(".sidepanel-map--planner__form , .uppercase-vz ,.hidden-xs ,.panel,.ns-field__label,.uppercase ,#fares-side-menu,.flex-column").css("color" ,"")
        $(".sidepanel-map--planner__form , .uppercase-vz ,.hidden-xs ,.panel,.ns-field__label,.uppercase ,#fares-side-menu,.flex-column").css("background-color" ,"")
        $("label,.ns-tabs__item,.primay-dark,.text-small, .ns-icon-angle-right ,.h1-main-title,.text-main-title ").css("color" ,"")
        $("label,.ns-tabs__item,.primay-dark,.text-small, .ns-icon-angle-right ,.h1-main-title,.text-main-title,.ns-session__nav--login ").css("background-color" ,"")
        $(".ns-layout__header--city , .ns-button,.text-info , .ns-icon-youtube,.ns-tabs, .button-tabs,.text-primary-hover,.ns-layout__footer__footer,.hidden-cl").css("color" ,"");
        $(".ns-layout__header--city , .ns-button,.text-info , .ns-icon-youtube ,.mb-d1-2 ,.px-d2 ,.ns-tabs__item ,.panel-schedules ,.ns-tabs, .button-tabs,.ns-layout__footer__footer,.hidden-cl").css("background-color" ,"")
        $(" .pb-d6 ,.panel-schedules ,.px-d2 ").css("color" ,"")  
        $("#iconorestablecer").css("color" ,"#1e1e1e");
        $("#iconorestablecer").css("background-color" ,"#fff")
        $("#textorestablecer").css("color" ,"#1e1e1e");
        $("#textorestablecer").css("background-color" ,"#fff")
        $("#linkhome").css( "color" ,"")
        $(".textomenu").css( "color","")
        $(".tablacss ").css( "color","")
        $(".tablacss ").css("background-color" ,"")
        $(".ns-icon-angle-left ").css("color" ,"")
        $(".ns-icon-angle-left ,.colorFondo  ").css("background-color" ,"")
    }
    }
  },

  mounted(){
    this.cargarContraste()
    var boton = document.getElementById("sublines_select");
    var left = this
    // Agregar un listener para el evento focus
    boton.addEventListener('focus', function() {
      var textoescuchar=left.$t('textoVoz.selectorLinea') 
      left.escucharAudio(textoescuchar )
      this.opcionSelectotLinea =0
    });
    document.addEventListener('keydown', function(event) {
      if (event.key === 'A' || event.key === 'a' ) {
        
        //left.desplegarOpciones(); // Mostrar las opciones al presionar Enter
        var textoescuchar=left.sublinesOptions[left.opcionSelectotLinea].label 
      
        left.escucharAudio(textoescuchar )
        left.opcionSelectotLinea++
      } else if (event.key === 'S' || event.key === 's' ) {
        if(left.opcionSelectotLinea > 0){
          left.opcionSelectotLinea--
          var textoescuchar=left.sublinesOptions[left.opcionSelectotLinea].label 
          left.escucharAudio(textoescuchar )
          
        } 
      }
      if (event.key === 'Enter' || event.keyCode === 13) {
        var opcion =0
        if(left.opcionSelectotLinea >0){
          opcion = left.opcionSelectotLinea-1
        }
        left.selectedSublineId=left.sublinesOptions[opcion].value
      } 
    });
  }
})
